import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCampaign } from "@/contexts/CampaignContext";

export default function Dashboard() {
  console.log('Dashboard component rendered');
  const { selectedCampaignId } = useCampaign();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats', selectedCampaignId],
    enabled: !!selectedCampaignId,
    queryFn: async () => {
      if (!selectedCampaignId) return null;

      // Total subscribers for this campaign
      const { count: total } = await supabase
        .from("subscribers")
        .select("*", { count: "exact", head: true })
        .eq("campaign_id", selectedCampaignId);

      // Active subscribers for this campaign
      const { count: active } = await supabase
        .from("subscribers")
        .select("*", { count: "exact", head: true })
        .eq("campaign_id", selectedCampaignId)
        .eq("status", "confirmed");

      // Get subscriber IDs for this campaign
      const { data: campaignSubscribers } = await supabase
        .from("subscribers")
        .select("id")
        .eq("campaign_id", selectedCampaignId);

      const subscriberIds = campaignSubscribers?.map(s => s.id) || [];

      // Email logs to determine total emails sent and unique days (filtered by campaign subscribers)
      const { data: logs } = subscriberIds.length > 0 ? await supabase
        .from("email_logs")
        .select("day_number")
        .in("subscriber_id", subscriberIds) : { data: [] };

      // Get unique days that have been sent
      const uniqueDays = new Set(logs?.map((l) => l.day_number) || []);
      const daysPassed = uniqueDays.size;

      // Total emails sent = active subscribers × days passed
      const totalEmailsSent = (active || 0) * daysPassed;

      // Engagement events (filtered by campaign subscribers)
      const { data: events } = subscriberIds.length > 0 ? await supabase
        .from("engagement_events")
        .select("event_type")
        .in("subscriber_id", subscriberIds) : { data: [] };

      const opens = events?.filter((e) => e.event_type === "open").length || 0;
      const clicks = events?.filter((e) => e.event_type === "click").length || 0;

      return {
        totalSubscribers: total || 0,
        activeSubscribers: active || 0,
        openRate: totalEmailsSent > 0 ? Math.round((opens / totalEmailsSent) * 100) : 0,
        clickRate: totalEmailsSent > 0 ? Math.round((clicks / totalEmailsSent) * 100) : 0,
      };
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Link to="/admin/subscribers">
          <Card className="cursor-pointer transition-all hover:shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Subscribers</CardTitle>
              <span className="text-2xl">👥</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalSubscribers ?? 0}</div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/subscribers?status=confirmed">
          <Card className="cursor-pointer transition-all hover:shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Subscribers</CardTitle>
              <span className="text-2xl">✅</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.activeSubscribers ?? 0}</div>
            </CardContent>
          </Card>
        </Link>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Rate</CardTitle>
            <span className="text-2xl">📧</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.openRate ?? 0}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Click Rate</CardTitle>
            <span className="text-2xl">🖱️</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.clickRate ?? 0}%</div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
